import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, optionsService, cursosService } from '../services/api';
import PasswordInput from '../components/PasswordInput';
import ConfirmPopup from '../components/ConfirmPopup';
import { analyzeNewPassword, newPasswordMatchesConfirm, validateNewPasswordForSubmit } from '../utils/passwordPolicy';
import { sanitizePhone } from '../utils/phone';
import { formatPersonName } from '../utils/personName';
import { formatDni, normalizeDni } from '../utils/dni';
import './Perfil.css';

/** Evita resetear selects hijos al cargar datos del servidor (solo vacía si el usuario cambió el padre). */
function geoParentChangedByUser(prev, current) {
  return (
    prev !== undefined &&
    String(prev) !== '' &&
    String(prev) !== String(current)
  );
}

const Perfil = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('datos-personales');
  const [cursos, setCursos] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmBaja, setConfirmBaja] = useState({ show: false, curso: null });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    dni: '',
    genero_id: '',
    fecha_nacimiento: '',
    telefono_celular: '',
    pais_id: '',
    pais_otro: '',
    provincia_id: '',
    provincia_otro: '',
    localidad_id: '',
    localidad_otro: '',
    barrio_id: '',
    barrio_otro: '',
    tipo_educacion_secundaria_id: '',
    nivel_estudio_id: '',
    situacion_laboral_id: '',
    area_trabajo_id: '',
    habilidades_ids: [],
    objetivo_curso_id: '',
    horas_dedicacion_id: '',
    tiene_computadora_internet: '',
    fuente_informacion_id: '',
  });

  const [options, setOptions] = useState({
    generos: [],
    paises: [],
    provincias: [],
    localidades: [],
    barrios: [],
    tiposEducacionSecundaria: [],
    nivelesEstudio: [],
    situacionesLaborales: [],
    areasTrabajo: [],
    habilidades: [],
    objetivosCurso: [],
    horasDedicacion: [],
    fuentesInformacion: [],
  });

  const [selectedSituacionLaboral, setSelectedSituacionLaboral] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  // Habilidades "Otros" (solo las que ingresó el usuario) para edición en Estudio y trabajo
  const [otrasHabilidades, setOtrasHabilidades] = useState(['']);

  const prevCascadePaisRef = useRef(undefined);
  const prevCascadeProvinciaRef = useRef(undefined);
  const prevCascadeLocalidadRef = useRef(undefined);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const loadData = async () => {
      try {
        const [userData, profileData, generos, paises, tiposEducacionSecundaria, nivelesEstudio, situacionesLaborales, areasTrabajo, habilidades, objetivosCurso, horasDedicacion, fuentesInformacion] = await Promise.all([
          authService.getProfile(),
          authService.getProfileExtended(),
          optionsService.getGeneros(),
          optionsService.getPaises(),
          optionsService.getTiposEducacionSecundaria(),
          optionsService.getNivelesEstudio(),
          optionsService.getSituacionesLaborales(),
          optionsService.getAreasTrabajo(),
          optionsService.getHabilidades(),
          optionsService.getObjetivosCurso(),
          optionsService.getHorasDedicacion(),
          optionsService.getFuentesInformacion(),
        ]);

        setUser(userData);
        setProfile(profileData);
        const rol = localStorage.getItem('selectedRole') || (userData.groups && userData.groups[0] && (typeof userData.groups[0] === 'object' ? userData.groups[0].name : userData.groups[0]));
        if (rol) setSelectedRole(rol);
        setOptions(prev => ({
          ...prev,
          generos,
          paises,
          tiposEducacionSecundaria,
          nivelesEstudio,
          situacionesLaborales,
          areasTrabajo,
          habilidades,
          objetivosCurso,
          horasDedicacion,
          fuentesInformacion,
        }));

        setFormData({
          first_name: formatPersonName(userData.first_name || ''),
          last_name: formatPersonName(userData.last_name || ''),
          dni: normalizeDni(profileData.dni || ''),
          genero_id: profileData.genero?.id || '',
          fecha_nacimiento: toDateOnly(profileData.fecha_nacimiento),
          telefono_celular: sanitizePhone(profileData.telefono_celular || ''),
          pais_id: profileData.pais?.id ? String(profileData.pais.id) : '',
          pais_otro: '',
          provincia_id: profileData.provincia?.id ? String(profileData.provincia.id) : '',
          provincia_otro: '',
          localidad_id: profileData.localidad?.id ? String(profileData.localidad.id) : '',
          localidad_otro: '',
          barrio_id: profileData.barrio?.id ? String(profileData.barrio.id) : '',
          barrio_otro: '',
          tipo_educacion_secundaria_id: profileData.tipo_educacion_secundaria?.id || '',
          nivel_estudio_id: profileData.nivel_estudio?.id || '',
          situacion_laboral_id: profileData.situacion_laboral?.id || '',
          area_trabajo_id: profileData.area_trabajo?.id || '',
          habilidades_ids: profileData.habilidades?.map(h => h.id) || [],
          objetivo_curso_id: profileData.objetivo_curso?.id || '',
          horas_dedicacion_id: profileData.horas_dedicacion?.id || '',
          tiene_computadora_internet: profileData.tiene_computadora_internet !== null && profileData.tiene_computadora_internet !== undefined ? String(profileData.tiene_computadora_internet) : '',
          fuente_informacion_id: profileData.fuente_informacion?.id || '',
        });
        const customNombres = (profileData.habilidades || []).filter(h => !(habilidades || []).some(o => o.id === h.id)).map(h => h.nombre);
        setOtrasHabilidades(customNombres.length ? customNombres : ['']);

        const esEstudiante = rol === 'Estudiante' || rol === 'Estudiantes';
        if (esEstudiante) {
          try {
            const misCursos = await cursosService.getMisCursos();
            setCursos(Array.isArray(misCursos) ? misCursos : []);
          } catch {
            setCursos([]);
          }
        } else {
          setCursos([]);
        }

        if (profileData.pais?.id) {
          const provincias = await optionsService.getProvincias(profileData.pais.id);
          setOptions(prev => ({ ...prev, provincias }));
        }
        if (profileData.provincia?.id) {
          const localidades = await optionsService.getLocalidades(profileData.provincia.id);
          setOptions(prev => ({ ...prev, localidades }));
        }
        if (profileData.localidad?.id) {
          const barrios = await optionsService.getBarrios(profileData.localidad.id);
          setOptions(prev => ({ ...prev, barrios }));
        }
      } catch (err) {
        console.error('Error al cargar perfil:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  useEffect(() => {
    const handleRoleChange = () => {
      const rol = localStorage.getItem('selectedRole');
      if (rol) setSelectedRole(rol);
    };
    window.addEventListener('roleChanged', handleRoleChange);
    return () => window.removeEventListener('roleChanged', handleRoleChange);
  }, []);

  const esEstudiante = selectedRole === 'Estudiante' || selectedRole === 'Estudiantes';

  useEffect(() => {
    const pid = formData.pais_id;
    const prev = prevCascadePaisRef.current;
    prevCascadePaisRef.current = pid;

    if (pid && pid !== 'otro') {
      optionsService.getProvincias(pid)
        .then((provincias) => {
          if (geoParentChangedByUser(prev, pid)) {
            setOptions((o) => ({ ...o, provincias, localidades: [], barrios: [] }));
            setFormData((fd) => ({
              ...fd,
              provincia_id: '',
              provincia_otro: '',
              localidad_id: '',
              localidad_otro: '',
              barrio_id: '',
              barrio_otro: '',
            }));
          } else {
            setOptions((o) => ({ ...o, provincias }));
          }
        })
        .catch(() => setOptions((o) => ({ ...o, provincias: [] })));
    } else if (pid === 'otro') {
      setOptions((o) => ({ ...o, provincias: [], localidades: [], barrios: [] }));
      setFormData((fd) => ({ ...fd, provincia_id: '', localidad_id: '', barrio_id: '', barrio_otro: '' }));
    } else {
      setOptions((o) => ({ ...o, provincias: [], localidades: [], barrios: [] }));
      setFormData((fd) => ({
        ...fd,
        provincia_id: '',
        provincia_otro: '',
        localidad_id: '',
        localidad_otro: '',
        barrio_id: '',
        barrio_otro: '',
      }));
    }
  }, [formData.pais_id]);

  useEffect(() => {
    const prid = formData.provincia_id;
    const prev = prevCascadeProvinciaRef.current;
    prevCascadeProvinciaRef.current = prid;

    if (prid && prid !== 'otro') {
      optionsService.getLocalidades(prid)
        .then((localidades) => {
          if (geoParentChangedByUser(prev, prid)) {
            setOptions((o) => ({ ...o, localidades, barrios: [] }));
            setFormData((fd) => ({
              ...fd,
              localidad_id: '',
              localidad_otro: '',
              barrio_id: '',
              barrio_otro: '',
            }));
          } else {
            setOptions((o) => ({ ...o, localidades }));
          }
        })
        .catch(() => setOptions((o) => ({ ...o, localidades: [] })));
    } else if (prid === 'otro') {
      setOptions((o) => ({ ...o, localidades: [], barrios: [] }));
      setFormData((fd) => ({ ...fd, localidad_id: '', barrio_id: '', barrio_otro: '' }));
    } else {
      setOptions((o) => ({ ...o, localidades: [], barrios: [] }));
      setFormData((fd) => ({
        ...fd,
        localidad_id: '',
        localidad_otro: '',
        barrio_id: '',
        barrio_otro: '',
      }));
    }
  }, [formData.provincia_id]);

  useEffect(() => {
    const lid = formData.localidad_id;
    const prev = prevCascadeLocalidadRef.current;
    prevCascadeLocalidadRef.current = lid;

    if (lid && lid !== 'otro') {
      optionsService.getBarrios(lid)
        .then((barrios) => {
          if (geoParentChangedByUser(prev, lid)) {
            setOptions((o) => ({ ...o, barrios }));
            setFormData((fd) => ({ ...fd, barrio_id: '', barrio_otro: '' }));
          } else {
            setOptions((o) => ({ ...o, barrios }));
          }
        })
        .catch(() => setOptions((o) => ({ ...o, barrios: [] })));
    } else {
      setOptions((o) => ({ ...o, barrios: [] }));
      setFormData((fd) => ({ ...fd, barrio_id: '', barrio_otro: '' }));
    }
  }, [formData.localidad_id]);

  useEffect(() => {
    const situacion = options.situacionesLaborales.find(s => s.id === parseInt(formData.situacion_laboral_id, 10));
    setSelectedSituacionLaboral(situacion || null);
    if (!situacion?.requiere_area) {
      setFormData(prev => ({ ...prev, area_trabajo_id: '' }));
    }
  }, [formData.situacion_laboral_id, options.situacionesLaborales]);

  const otrosId = options.habilidades?.find(h => (h.nombre || '').toLowerCase() === 'otros')?.id;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'habilidades_ids') {
        const id = parseInt(value, 10);
        if (id === otrosId && !checked) {
          setOtrasHabilidades(['']);
          setFormData(prev => ({
            ...prev,
            habilidades_ids: prev.habilidades_ids.filter(i => i !== id && (options.habilidades || []).some(o => o.id === i)),
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            habilidades_ids: prev.habilidades_ids.includes(id)
              ? prev.habilidades_ids.filter(i => i !== id)
              : [...prev.habilidades_ids, id],
          }));
        }
      }
    } else {
      const finalValue = name === 'telefono_celular'
        ? sanitizePhone(value)
        : name === 'dni'
          ? normalizeDni(value)
          : value;
      setFormData(prev => ({ ...prev, [name]: finalValue }));
    }
    setError('');
    setSuccess('');
  };

  const getRequiredErrorsForSection = (section) => {
    const err = [];
    if (section === 'datos-personales') {
      if (!formData.first_name?.trim()) err.push('Nombre/s');
      if (!formData.last_name?.trim()) err.push('Apellido/s');
      if (!formData.dni?.trim()) err.push('DNI');
      if (!formData.genero_id) err.push('Género');
      if (!formData.fecha_nacimiento) err.push('Fecha de nacimiento');
      if (!formData.telefono_celular?.trim()) err.push('Teléfono celular');
      const hasPais = formData.pais_id || (formData.pais_otro || '').trim();
      const hasProvincia = formData.provincia_id || (formData.provincia_otro || '').trim();
      const hasLocalidad = formData.localidad_id || (formData.localidad_otro || '').trim();
      const hasBarrio = formData.barrio_id || (formData.barrio_otro || '').trim();
      if (!hasPais) err.push('País');
      if (!hasProvincia) err.push('Provincia');
      if (!hasLocalidad) err.push('Partido');
      if (!hasBarrio) err.push('Barrio');
    }
    if (section === 'estudio-trabajo') {
      if (!formData.tipo_educacion_secundaria_id) err.push('Tipo de educación secundaria');
      if (!formData.nivel_estudio_id) err.push('Nivel de estudio');
      if (!formData.situacion_laboral_id) err.push('Situación laboral');
      if (selectedSituacionLaboral?.requiere_area && !formData.area_trabajo_id) err.push('Área de trabajo');
    }
    if (section === 'informacion-adicional') {
      if (!formData.objetivo_curso_id) err.push('Objetivo del curso');
      if (!formData.horas_dedicacion_id) err.push('Horas de dedicación');
      if (formData.tiene_computadora_internet === '') err.push('¿Tiene computadora e internet?');
      if (!formData.fuente_informacion_id) err.push('Fuente de información');
    }
    return err;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const section = editingSection;
    if (!section) return;

    const requiredErrors = getRequiredErrorsForSection(section);
    if (requiredErrors.length > 0) {
      setError('Completá todos los campos obligatorios: ' + requiredErrors.join(', ') + '.');
      return;
    }

    setSaving(true);
    try {
      let payload = {};

      if (section === 'datos-personales') {
        payload = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          dni: normalizeDni(formData.dni.trim()),
          genero_id: parseInt(formData.genero_id, 10),
          fecha_nacimiento: formData.fecha_nacimiento || null,
          telefono_celular: formData.telefono_celular.trim(),
          pais_id: formData.pais_id === 'otro' ? null : (formData.pais_id ? parseInt(formData.pais_id, 10) : null),
          pais_otro: (formData.pais_otro || '').trim(),
          provincia_id: formData.provincia_id === 'otro' ? null : (formData.provincia_id ? parseInt(formData.provincia_id, 10) : null),
          provincia_otro: (formData.provincia_otro || '').trim(),
          localidad_id: formData.localidad_id === 'otro' ? null : (formData.localidad_id ? parseInt(formData.localidad_id, 10) : null),
          localidad_otro: (formData.localidad_otro || '').trim(),
          barrio_id: formData.barrio_id === 'otro' || !formData.barrio_id ? null : parseInt(formData.barrio_id, 10),
          barrio_otro: (formData.barrio_otro || '').trim(),
        };
      }

      if (section === 'estudio-trabajo') {
        let habilidadesIdsFinal = formData.habilidades_ids || [];
        const textosOtras = otrasHabilidades.filter(t => t && t.trim());
        if (textosOtras.length > 0) {
          const customIds = [];
          for (const nombre of textosOtras) {
            try {
              const { id } = await optionsService.getOrCreateHabilidad(nombre);
              customIds.push(id);
            } catch (err) {
              setError(err.response?.data?.nombre?.[0] || 'Error al guardar una habilidad. Intentá de nuevo.');
              setSaving(false);
              return;
            }
          }
          habilidadesIdsFinal = (formData.habilidades_ids || []).filter(id => id !== otrosId).concat(customIds);
        }
        payload = {
          tipo_educacion_secundaria_id: parseInt(formData.tipo_educacion_secundaria_id, 10),
          nivel_estudio_id: parseInt(formData.nivel_estudio_id, 10),
          situacion_laboral_id: parseInt(formData.situacion_laboral_id, 10),
          area_trabajo_id: formData.area_trabajo_id ? parseInt(formData.area_trabajo_id, 10) : null,
          habilidades_ids: habilidadesIdsFinal.map(id => parseInt(id, 10)),
        };
      }

      if (section === 'informacion-adicional') {
        payload = {
          objetivo_curso_id: parseInt(formData.objetivo_curso_id, 10),
          horas_dedicacion_id: parseInt(formData.horas_dedicacion_id, 10),
          tiene_computadora_internet: formData.tiene_computadora_internet === 'true',
          fuente_informacion_id: parseInt(formData.fuente_informacion_id, 10),
        };
      }

      await authService.updateProfile(payload);
      const [userData, profileData] = await Promise.all([
        authService.getProfile(),
        authService.getProfileExtended(),
      ]);
      setUser(userData);
      setProfile(profileData);
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        currentUser.profile_complete = true;
        localStorage.setItem('user', JSON.stringify(currentUser));
      }
      setSuccess('Perfil actualizado correctamente.');
      setEditingSection(null);
    } catch (err) {
      const msg = (err.response?.data && (typeof err.response.data === 'string' ? err.response.data : Object.values(err.response.data).flat()[0])) || 'Error al guardar. Intentá de nuevo.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.newPasswordConfirm) {
      setPasswordError('Completá todos los campos.');
      return;
    }
    const policyErr = validateNewPasswordForSubmit(
      passwordForm.newPassword,
      passwordForm.newPasswordConfirm
    );
    if (policyErr) {
      setPasswordError(policyErr);
      return;
    }
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');
    try {
      await authService.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordForm({ currentPassword: '', newPassword: '', newPasswordConfirm: '' });
      setPasswordSuccess('Contraseña actualizada correctamente.');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Error al cambiar la contraseña. Verificá la contraseña actual.');
    } finally {
      setPasswordLoading(false);
    }
  };

  /** Formatea una fecha solo-día (ej. fecha nacimiento) sin desfase UTC: usa día como calendario local. */
  const formatDate = (dateString) => {
    if (!dateString) return 'No especificado';
    const str = String(dateString).trim();
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match;
      return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return new Date(dateString).toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  /** Normaliza fecha del API a YYYY-MM-DD para input type="date" (evita desfase por UTC). */
  const toDateOnly = (dateString) => {
    if (!dateString) return '';
    const str = String(dateString).trim();
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? match[0] : str.substring(0, 10) || '';
  };

  const passwordPolicyChecks = useMemo(() => {
    const p = passwordForm.newPassword;
    const c = passwordForm.newPasswordConfirm;
    const a = analyzeNewPassword(p);
    return {
      ...a,
      matchesConfirm: newPasswordMatchesConfirm(p, c),
    };
  }, [passwordForm.newPassword, passwordForm.newPasswordConfirm]);

  if (loading) {
    return (
      <div className="perfil-loading">
        <div className="loading-spinner"></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  return (
    <>
    <div className="perfil-container">
      <div className="perfil-header">
        <h1>Mi Perfil</h1>
        <p>Consultá y editá tu información personal cuando lo necesites.</p>
      </div>

      <div className="perfil-sections-nav">
        <button
          className={`section-nav-btn ${activeSection === 'datos-personales' ? 'active' : ''}`}
          onClick={() => { setActiveSection('datos-personales'); setEditingSection(null); }}
        >
          👤 Datos Personales
        </button>
        <button
          className={`section-nav-btn ${activeSection === 'estudio-trabajo' ? 'active' : ''}`}
          onClick={() => { setActiveSection('estudio-trabajo'); setEditingSection(null); }}
        >
          🎓 Estudio y Trabajo
        </button>
        <button
          className={`section-nav-btn ${activeSection === 'informacion-adicional' ? 'active' : ''}`}
          onClick={() => { setActiveSection('informacion-adicional'); setEditingSection(null); }}
        >
          ℹ️ Información Adicional
        </button>
        {esEstudiante && (
          <button
            className={`section-nav-btn ${activeSection === 'mis-cursos' ? 'active' : ''}`}
            onClick={async () => {
              setActiveSection('mis-cursos');
              setEditingSection(null);
              try {
                const misCursos = await cursosService.getMisCursos();
                setCursos(Array.isArray(misCursos) ? misCursos : []);
              } catch {
                setCursos([]);
              }
            }}
          >
            📚 Mis Cursos
          </button>
        )}
        <button
          className={`section-nav-btn ${activeSection === 'cambiar-contrasena' ? 'active' : ''}`}
          onClick={() => { setActiveSection('cambiar-contrasena'); setEditingSection(null); }}
        >
          🔒 Cambiar contraseña
        </button>
      </div>

      <div className="perfil-content">
        <ConfirmPopup
          open={Boolean(error)}
          title="Error"
          message={error}
          confirmLabel="Aceptar"
          cancelLabel={null}
          onConfirm={() => setError('')}
          onCancel={() => setError('')}
          variant="danger"
        />
        {success && <div className="perfil-form-success">{success}</div>}

        {activeSection === 'datos-personales' && (
          <div className="perfil-section">
            <div className="perfil-section-header">
              <h2 className="perfil-section-title">Datos Personales</h2>
              {editingSection !== 'datos-personales' ? (
                <button type="button" className="perfil-edit-btn" onClick={() => setEditingSection('datos-personales')} title="Editar">
                  <span className="perfil-edit-icon" aria-hidden>✏️</span> Editar
                </button>
              ) : null}
            </div>
            {editingSection === 'datos-personales' ? (
              <form onSubmit={handleSave} className="perfil-form-grid">
                <div className="perfil-form-group">
                  <label className="perfil-label">Nombre/s <span className="required">*</span></label>
                  <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} className="perfil-input" required />
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Apellido/s <span className="required">*</span></label>
                  <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} className="perfil-input" required />
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Email</label>
                  <input type="email" value={user?.email || ''} readOnly disabled className="perfil-input" style={{ opacity: 0.8 }} />
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">DNI <span className="required">*</span></label>
                  <input type="text" name="dni" value={formData.dni} onChange={handleChange} className="perfil-input" pattern="[A-Za-z0-9]{7,20}" maxLength="20" required title="Solo letras y números, entre 7 y 20 caracteres (sin espacios ni signos)" />
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Género <span className="required">*</span></label>
                  <select name="genero_id" value={formData.genero_id} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    {options.generos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                  </select>
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Fecha de Nacimiento <span className="required">*</span></label>
                  <input type="date" name="fecha_nacimiento" value={formData.fecha_nacimiento} onChange={handleChange} className="perfil-input" max={new Date().toISOString().split('T')[0]} required />
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Teléfono Celular <span className="required">*</span></label>
                  <input type="tel" name="telefono_celular" value={formData.telefono_celular} onChange={handleChange} className="perfil-input" required maxLength={20} pattern="\+?[0-9]+" title="Solo números; opcionalmente un + al inicio" />
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">País <span className="required">*</span></label>
                  <select name="pais_id" value={formData.pais_id} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    {options.paises.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    <option value="otro">Otro</option>
                  </select>
                  {formData.pais_id === 'otro' && (
                    <input type="text" name="pais_otro" value={formData.pais_otro} onChange={handleChange} placeholder="Escribí tu país" className="perfil-input perfil-input--mt" maxLength={150} />
                  )}
                </div>
                {(formData.pais_id || formData.pais_otro) && (
                  <div className="perfil-form-group">
                    <label className="perfil-label">Provincia <span className="required">*</span></label>
                    <select name="provincia_id" value={formData.provincia_id} onChange={handleChange} className="perfil-select" required>
                      <option value="">Seleccionar...</option>
                      {options.provincias.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.provincia_id === 'otro' && (
                      <input type="text" name="provincia_otro" value={formData.provincia_otro} onChange={handleChange} placeholder="Escribí tu provincia" className="perfil-input perfil-input--mt" maxLength={150} />
                    )}
                  </div>
                )}
                {(formData.provincia_id || formData.provincia_otro) && (
                  <div className="perfil-form-group">
                    <label className="perfil-label">Partido <span className="required">*</span></label>
                    <select name="localidad_id" value={formData.localidad_id} onChange={handleChange} className="perfil-select" required>
                      <option value="">Seleccionar...</option>
                      {options.localidades.map(l => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.localidad_id === 'otro' && (
                      <input type="text" name="localidad_otro" value={formData.localidad_otro} onChange={handleChange} placeholder="Escribí tu partido" className="perfil-input perfil-input--mt" maxLength={150} />
                    )}
                  </div>
                )}
                {(formData.localidad_id || formData.localidad_otro) && (
                  <div className="perfil-form-group">
                    <label className="perfil-label">Barrio <span className="required">*</span></label>
                    <select name="barrio_id" value={formData.barrio_id} onChange={handleChange} className="perfil-select" required>
                      <option value="">Seleccionar...</option>
                      {options.barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.barrio_id === 'otro' && (
                      <input type="text" name="barrio_otro" value={formData.barrio_otro} onChange={handleChange} placeholder="Escribí tu barrio" className="perfil-input perfil-input--mt" maxLength={150} />
                    )}
                  </div>
                )}
                <div className="perfil-form-group full-width perfil-actions">
                  <button type="submit" className="perfil-btn perfil-btn-primary" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                  <button type="button" className="perfil-btn perfil-btn-secondary" onClick={() => setEditingSection(null)}>
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="perfil-info-grid">
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Nombre/s</span>
                    <span className="perfil-info-value">{formatPersonName(user?.first_name) || '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Apellido/s</span>
                    <span className="perfil-info-value">{formatPersonName(user?.last_name) || '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Email</span>
                    <span className="perfil-info-value">{user?.email || '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">DNI</span>
                    <span className="perfil-info-value">{profile?.dni ? formatDni(profile.dni) : '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Género</span>
                    <span className="perfil-info-value">{profile?.genero?.nombre || '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Fecha de Nacimiento</span>
                    <span className="perfil-info-value">{formatDate(profile?.fecha_nacimiento)}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Teléfono Celular</span>
                    <span className="perfil-info-value">{profile?.telefono_celular || '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">País</span>
                    <span className="perfil-info-value">{profile?.pais?.nombre ?? '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Provincia</span>
                    <span className="perfil-info-value">{profile?.provincia?.nombre ?? '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Partido</span>
                    <span className="perfil-info-value">{profile?.localidad?.nombre ?? '—'}</span>
                  </div>
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Barrio</span>
                    <span className="perfil-info-value">{profile?.barrio?.nombre ?? '—'}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeSection === 'cambiar-contrasena' && (
          <div className="perfil-section">
            <h2 className="perfil-section-title">Cambiar contraseña</h2>
            <p className="perfil-section-desc">Ingresá tu contraseña actual y la nueva contraseña que quieras usar.</p>
            <ConfirmPopup
              open={Boolean(passwordError)}
              title="Error"
              message={passwordError}
              confirmLabel="Aceptar"
              cancelLabel={null}
              onConfirm={() => setPasswordError('')}
              onCancel={() => setPasswordError('')}
              variant="danger"
            />
            {passwordSuccess && <div className="perfil-form-success">{passwordSuccess}</div>}
            <div className="perfil-password-row">
              <form onSubmit={handlePasswordSubmit} className="perfil-form-grid perfil-password-form">
                <div className="perfil-form-group">
                  <label className="perfil-label">Contraseña actual <span className="required">*</span></label>
                  <PasswordInput
                    value={passwordForm.currentPassword}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value })); setPasswordError(''); setPasswordSuccess(''); }}
                    className="perfil-input"
                    autoComplete="current-password"
                    required
                  />
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Contraseña nueva <span className="required">*</span></label>
                  <PasswordInput
                    value={passwordForm.newPassword}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, newPassword: e.target.value })); setPasswordError(''); setPasswordSuccess(''); }}
                    className="perfil-input"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Repetir contraseña nueva <span className="required">*</span></label>
                  <PasswordInput
                    value={passwordForm.newPasswordConfirm}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, newPasswordConfirm: e.target.value })); setPasswordError(''); setPasswordSuccess(''); }}
                    className="perfil-input"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </div>
                <div className="perfil-form-group perfil-actions">
                  <button type="submit" className="perfil-btn perfil-btn-primary" disabled={passwordLoading}>
                    {passwordLoading ? 'Guardando...' : 'Cambiar contraseña'}
                  </button>
                </div>
              </form>
              <aside className="perfil-password-rules" aria-live="polite">
                <p className="perfil-password-rules-title">Tu contraseña nueva debe:</p>
                <ul>
                  <li
                    className={
                      passwordPolicyChecks.minLength
                        ? 'perfil-password-rule perfil-password-rule--ok'
                        : 'perfil-password-rule'
                    }
                  >
                    Tener al menos 8 caracteres
                  </li>
                  <li
                    className={
                      passwordPolicyChecks.hasUpper
                        ? 'perfil-password-rule perfil-password-rule--ok'
                        : 'perfil-password-rule'
                    }
                  >
                    Incluir al menos una letra mayúscula
                  </li>
                  <li
                    className={
                      passwordPolicyChecks.hasLower
                        ? 'perfil-password-rule perfil-password-rule--ok'
                        : 'perfil-password-rule'
                    }
                  >
                    Incluir al menos una letra minúscula
                  </li>
                  <li
                    className={
                      passwordPolicyChecks.hasSpecial
                        ? 'perfil-password-rule perfil-password-rule--ok'
                        : 'perfil-password-rule'
                    }
                  >
                    Incluir al menos un símbolo:{' '}
                    <span className="perfil-password-rule-symbols">@ # - _ $ ! % * ? & + = . , :</span>
                  </li>
                  <li
                    className={
                      passwordPolicyChecks.matchesConfirm
                        ? 'perfil-password-rule perfil-password-rule--ok'
                        : 'perfil-password-rule'
                    }
                  >
                    Coincidir en ambos campos de contraseña nueva
                  </li>
                </ul>
              </aside>
            </div>
          </div>
        )}

        {activeSection === 'estudio-trabajo' && (
          <div className="perfil-section">
            <div className="perfil-section-header">
              <h2 className="perfil-section-title">Estudio y Trabajo</h2>
              {editingSection !== 'estudio-trabajo' ? (
                <button type="button" className="perfil-edit-btn" onClick={() => setEditingSection('estudio-trabajo')} title="Editar">
                  <span className="perfil-edit-icon" aria-hidden>✏️</span> Editar
                </button>
              ) : null}
            </div>
            {editingSection === 'estudio-trabajo' ? (
              <form onSubmit={handleSave} className="perfil-form-grid">
                <div className="perfil-form-group">
                  <label className="perfil-label">Tipo de Educación Secundaria <span className="required">*</span></label>
                  <select name="tipo_educacion_secundaria_id" value={formData.tipo_educacion_secundaria_id} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    {options.tiposEducacionSecundaria.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                  </select>
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Nivel de Estudio <span className="required">*</span></label>
                  <select name="nivel_estudio_id" value={formData.nivel_estudio_id} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    {options.nivelesEstudio.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                  </select>
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Situación Laboral <span className="required">*</span></label>
                  <select name="situacion_laboral_id" value={formData.situacion_laboral_id} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    {options.situacionesLaborales.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
                {selectedSituacionLaboral?.requiere_area && (
                  <div className="perfil-form-group">
                    <label className="perfil-label">Área de Trabajo <span className="required">*</span></label>
                    <select name="area_trabajo_id" value={formData.area_trabajo_id} onChange={handleChange} className="perfil-select" required>
                      <option value="">Seleccionar...</option>
                      {options.areasTrabajo.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                    </select>
                  </div>
                )}
                <div className="perfil-form-group full-width">
                  <label className="perfil-label">Habilidades (opcional)</label>
                  <div className="perfil-checkboxes">
                    {[...(options.habilidades || []), ...(profile?.habilidades?.filter(h => !(options.habilidades || []).some(o => o.id === h.id)) || [])].map(h => (
                      <label key={h.id} className="perfil-checkbox-label">
                        <input
                          type="checkbox"
                          name="habilidades_ids"
                          value={h.id}
                          checked={h.id === otrosId ? (formData.habilidades_ids.includes(h.id) || otrasHabilidades.some(t => t && t.trim())) : formData.habilidades_ids.includes(h.id)}
                          onChange={handleChange}
                        />
                        <span>{h.nombre}</span>
                      </label>
                    ))}
                  </div>
                  {(formData.habilidades_ids.includes(otrosId) || otrasHabilidades.some(t => t && t.trim())) && (
                    <div className="perfil-otras-habilidades">
                      <span className="perfil-label perfil-otras-label">Otras habilidades (una por línea):</span>
                      {otrasHabilidades.map((valor, idx) => (
                        <div key={idx} className="perfil-otras-row">
                          <input
                            type="text"
                            value={valor}
                            onChange={(e) => {
                              const next = [...otrasHabilidades];
                              next[idx] = e.target.value;
                              setOtrasHabilidades(next);
                            }}
                            placeholder="Ej: Excel, Inglés..."
                            className="perfil-input perfil-otras-input"
                          />
                          {idx === otrasHabilidades.length - 1 ? (
                            <button
                              type="button"
                              className="perfil-otras-add"
                              onClick={() => setOtrasHabilidades(prev => [...prev, ''])}
                              title="Agregar otra habilidad"
                              aria-label="Agregar otra habilidad"
                            >
                              +
                            </button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="perfil-form-group full-width perfil-actions">
                  <button type="submit" className="perfil-btn perfil-btn-primary" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                  <button type="button" className="perfil-btn perfil-btn-secondary" onClick={() => setEditingSection(null)}>
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <div className="perfil-info-grid">
                <div className="perfil-info-item">
                  <span className="perfil-info-label">Tipo de Educación Secundaria</span>
                  <span className="perfil-info-value">{profile?.tipo_educacion_secundaria?.nombre || '—'}</span>
                </div>
                <div className="perfil-info-item">
                  <span className="perfil-info-label">Nivel de Estudio</span>
                  <span className="perfil-info-value">{profile?.nivel_estudio?.nombre || '—'}</span>
                </div>
                <div className="perfil-info-item">
                  <span className="perfil-info-label">Situación Laboral</span>
                  <span className="perfil-info-value">{profile?.situacion_laboral?.nombre || '—'}</span>
                </div>
                {profile?.area_trabajo && (
                  <div className="perfil-info-item">
                    <span className="perfil-info-label">Área de Trabajo</span>
                    <span className="perfil-info-value">{profile.area_trabajo.nombre}</span>
                  </div>
                )}
                <div className="perfil-info-item full-width">
                  <span className="perfil-info-label">Habilidades</span>
                  <div className="perfil-info-value">
                    {profile?.habilidades?.length > 0 ? (
                      <div className="perfil-habilidades-list">
                        {profile.habilidades.map((h, i) => (
                          <span key={h.id || i} className="perfil-habilidad-badge">{h.nombre}</span>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeSection === 'informacion-adicional' && (
          <div className="perfil-section">
            <div className="perfil-section-header">
              <h2 className="perfil-section-title">Información Adicional</h2>
              {editingSection !== 'informacion-adicional' ? (
                <button type="button" className="perfil-edit-btn" onClick={() => setEditingSection('informacion-adicional')} title="Editar">
                  <span className="perfil-edit-icon" aria-hidden>✏️</span> Editar
                </button>
              ) : null}
            </div>
            {editingSection === 'informacion-adicional' ? (
              <form onSubmit={handleSave} className="perfil-form-grid">
                <div className="perfil-form-group">
                  <label className="perfil-label">Objetivo del Curso <span className="required">*</span></label>
                  <select name="objetivo_curso_id" value={formData.objetivo_curso_id} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    {options.objetivosCurso.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
                  </select>
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Horas de Dedicación Semanal <span className="required">*</span></label>
                  <select name="horas_dedicacion_id" value={formData.horas_dedicacion_id} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    {options.horasDedicacion.map(h => <option key={h.id} value={h.id}>{h.nombre}</option>)}
                  </select>
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">¿Tiene computadora e internet? <span className="required">*</span></label>
                  <select name="tiene_computadora_internet" value={formData.tiene_computadora_internet} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="perfil-form-group">
                  <label className="perfil-label">Fuente de Información <span className="required">*</span></label>
                  <select name="fuente_informacion_id" value={formData.fuente_informacion_id} onChange={handleChange} className="perfil-select" required>
                    <option value="">Seleccionar...</option>
                    {options.fuentesInformacion.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                  </select>
                </div>
                <div className="perfil-form-group full-width perfil-actions">
                  <button type="submit" className="perfil-btn perfil-btn-primary" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                  <button type="button" className="perfil-btn perfil-btn-secondary" onClick={() => setEditingSection(null)}>
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <div className="perfil-info-grid">
                <div className="perfil-info-item">
                  <span className="perfil-info-label">Objetivo del Curso</span>
                  <span className="perfil-info-value">{profile?.objetivo_curso?.nombre || '—'}</span>
                </div>
                <div className="perfil-info-item">
                  <span className="perfil-info-label">Horas de Dedicación Semanal</span>
                  <span className="perfil-info-value">{profile?.horas_dedicacion?.nombre || '—'}</span>
                </div>
                <div className="perfil-info-item">
                  <span className="perfil-info-label">¿Tiene computadora e internet?</span>
                  <span className="perfil-info-value">
                    {profile?.tiene_computadora_internet === true ? 'Sí' : profile?.tiene_computadora_internet === false ? 'No' : '—'}
                  </span>
                </div>
                <div className="perfil-info-item">
                  <span className="perfil-info-label">Fuente de Información</span>
                  <span className="perfil-info-value">{profile?.fuente_informacion?.nombre || '—'}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {esEstudiante && activeSection === 'mis-cursos' && (
          <div className="perfil-section">
            <h2 className="perfil-section-title">Mis Cursos</h2>
            {cursos && cursos.length > 0 ? (
              <div className="perfil-cursos-grid perfil-cursos-grid-mis-cursos">
                {cursos.map((curso, index) => {
                  const nivelNombre = curso.nivel_nombre || (curso.nivel && typeof curso.nivel === 'object' && curso.nivel.nombre ? curso.nivel.nombre : null);
                  return (
                  <div key={curso.id || index} className="perfil-curso-card">
                    {nivelNombre && (
                      <span className="perfil-curso-badge-nivel" aria-label={`Nivel: ${nivelNombre}`}>
                        {nivelNombre}
                      </span>
                    )}
                    <div className="perfil-curso-card-header">
                      <h3 className="perfil-curso-title">{curso.nombre || 'Curso sin nombre'}</h3>
                    </div>
                    <div className="perfil-curso-info perfil-curso-meta">
                      <div className="perfil-curso-meta-row">
                        <span className="perfil-curso-meta-label">Fecha de inscripción:</span>
                        <span className="perfil-curso-meta-value">
                          {curso.fecha_inscripcion
                            ? (typeof curso.fecha_inscripcion === 'string'
                                ? new Date(curso.fecha_inscripcion.includes('T') ? curso.fecha_inscripcion : curso.fecha_inscripcion + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                : curso.fecha_inscripcion)
                            : '—'}
                        </span>
                      </div>
                      <div className="perfil-curso-meta-row">
                        <span className="perfil-curso-meta-label">Estado:</span>
                        <span className={`perfil-curso-estado perfil-curso-estado-${(curso.estado || 'En curso').toLowerCase().replace(/\s/g, '-')}`}>
                          {curso.estado || 'En curso'}
                        </span>
                      </div>
                      {(curso.certificado === true || curso.certificado === 'true') && (
                        <div className="perfil-curso-certificado-wrap">
                          <button
                            type="button"
                            className="perfil-curso-certificado-btn"
                            onClick={async () => {
                              try {
                                const blob = await cursosService.getCertificadoPdf(curso.id);
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank', 'noopener');
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                            title="Ver o descargar certificado"
                            aria-label="Ver certificado"
                          >
                            🏆 Certificado
                          </button>
                        </div>
                      )}
                      {(curso.estado === 'En curso') && (
                        <div className="perfil-curso-meta-row perfil-curso-actions">
                          <button
                            type="button"
                            className="perfil-curso-baja-btn"
                            onClick={() => setConfirmBaja({ show: true, curso })}
                            title="Dejar el curso"
                            aria-label="Darme de baja"
                          >
                            Darme de baja
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            ) : (
              <div className="perfil-empty-state">
                <div className="perfil-empty-icon">📚</div>
                <h3>No estás inscripto en ningún curso.</h3>
                <p>Explorá los cursos disponibles y comenzá tu aprendizaje</p>
                <button className="perfil-empty-button" onClick={() => navigate('/dashboard/cursos-disponibles')}>
                  Ver Cursos Disponibles
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      </div>

      <ConfirmPopup
        open={confirmBaja.show}
        title="Darme de baja del curso"
        message={confirmBaja.curso ? `¿Estás seguro de que querés darte de baja de "${confirmBaja.curso.nombre || 'este curso'}"? Dejarás de estar inscripto.` : ''}
        confirmLabel="Sí, darme de baja"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={async () => {
          if (!confirmBaja.curso) return;
          try {
            await cursosService.darseDeBajaCurso(confirmBaja.curso.id);
            setConfirmBaja({ show: false, curso: null });
            setSuccess('Te has dado de baja del curso.');
            const misCursos = await cursosService.getMisCursos();
            setCursos(Array.isArray(misCursos) ? misCursos : []);
          } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Error al darte de baja';
            setError(msg);
            setConfirmBaja({ show: false, curso: null });
          }
        }}
        onCancel={() => setConfirmBaja({ show: false, curso: null })}
      />
    </>
  );
};

export default Perfil;
